import React, { useEffect, useRef } from 'react';
import { useTranslation } from '../components/TranslationContext';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import '../styles/histoire.css';
import '../styles/facility.css';
import '../styles/certifications.css';
import '../styles/products.css';
import '../styles/root.css';
import timelineImage1 from '../assets/usine/Marine & Moi-193.jpg';
import timelineImage2 from '../assets/usine/Marine & Moi-7.jpg';
import timelineImage3 from '../assets/usine/Marine & Moi-34.jpg';
import facilityImage1 from '../assets/usine/Marine & Moi-190.jpg';
import facilityImage2 from '../assets/usine/Marine & Moi-34.jpg';
import facilityImage3 from '../assets/usine/Marine & Moi-62.jpg';
import facilityImage4 from '../assets/usine/Marine & Moi-15.jpg';
import certImage1 from '../assets/certifications/wrap.png';
import certImage2 from '../assets/certifications/iso9001.jpg';
import certImage3 from '../assets/certifications/oekotex.png';
import certImage4 from '../assets/certifications/gots.png';
import productImage1 from '../assets/usine/Marine & Moi-132.jpg';
import productImage2 from '../assets/usine/Marine & Moi-128.jpg';
import productImage3 from '../assets/usine/uniforme-cuisine.jpg';

const Entreprise = () => {
  const timelineRefs = useRef([]);
  const facilityRef = useRef();
  const { translate } = useTranslation();
  const entreprise = translate('entreprise');

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            if (entry.target.classList.contains('facility-stats-number')) {
              animateNumber(entry.target);
            }
          }
        });
      },
      {
        threshold: 0.1
      }
    );

    timelineRefs.current.forEach(ref => {
      if (ref) {
        observer.observe(ref);
      }
    });

    const facilityNumbers = facilityRef.current.querySelectorAll('.facility-stats-number');
    facilityNumbers.forEach(number => {
      observer.observe(number);
    });

    return () => {
      timelineRefs.current.forEach(ref => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
      facilityNumbers.forEach(number => {
        observer.unobserve(number);
      });
    };
  }, []);

  const animateNumber = (element) => {
    const target = parseInt(element.getAttribute('data-target'), 10);
    const duration = 2000;
    const frameRate = 1000 / 60;
    const totalFrames = duration / frameRate;
    let currentFrame = 0;

    const increment = target / totalFrames;

    const animate = () => {
      currentFrame++;
      const newValue = Math.round(increment * currentFrame);
      element.textContent = newValue;
      if (currentFrame < totalFrames) {
        requestAnimationFrame(animate);
      } else {
        element.textContent = target;
      }
    };

    animate();
  };

  return (
    <>
      <Helmet>
        <title>Marine & Moi - L'Entreprise</title>
        <meta name="description" content="Découvrez l'histoire et les installations de Marine & Moi, votre partenaire de confiance dans le textile premium." />
        <meta name="keywords" content="histoire, installations, textile, premium, partenaire, confiance" />
      </Helmet>
      <div className="timeline-wrapper">
        <h1>{entreprise.historyTitle}</h1>
        <div className="timeline-line"></div>
        <div className="timeline-row timeline-row-left">
          <section
            ref={el => (timelineRefs.current[0] = el)}
          >
            <img src={timelineImage1} alt="Fondée en 2008" className="timeline-image" />
            <div className="section-details">
            </div>
            <p>{entreprise.history1}</p>
          </section>
        </div>
        <div className="timeline-row timeline-row-right">
          <section
            ref={el => (timelineRefs.current[1] = el)}
          >
            <img src={timelineImage2} alt="Fabrication Textile" className="timeline-image" />
            <div className="section-details">
            </div>
            <p>{entreprise.history2}</p>
          </section>
        </div>
        <div className="timeline-row timeline-row-left">
          <section
            ref={el => (timelineRefs.current[2] = el)}
          >
            <img src={timelineImage3} alt="Contribution à la croissance de Madagascar" className="timeline-image" />
            <div className="section-details">
            </div>
            <p>{entreprise.history3}</p>
          </section>
        </div>
      </div>
      <section className="facility" ref={facilityRef}>
        <h1>{entreprise.facilitiesTitle}</h1>
        <div className="facility-cards">
          <div className="facility-card">
            <div className="facility-stats">
              <img src={facilityImage1} alt="Installations" className="facility-image" />
              <span className="facility-stats-number" data-target="7500">0</span> m²
            </div>
            <div className="facility-text">
              {entreprise.facilities1}
            </div>
          </div>
          <div className="facility-card alternate">
            <div className="facility-text">
              {entreprise.facilities2}
            </div>
            <div className="facility-stats">
              <img src={facilityImage2} alt="Employés" className="facility-image" />
              <span className="facility-stats-number" data-target="1200">0</span> employés
            </div>
          </div>
          <div className="facility-card">
            <div className="facility-stats">
              <img src={facilityImage3} alt="Lignes de production" className="facility-image" />
              <span className="facility-stats-number" data-target="26">0</span> lignes de production
            </div>
            <div className="facility-text">
              {entreprise.facilities3}
            </div>
          </div>
          <div className="facility-card alternate">
            <div className="facility-text">
              {entreprise.facilities4}
            </div>
            <div className="facility-stats">
              <img src={facilityImage4} alt="Unité de broderie" className="facility-image" />
              <span className="facility-stats-number" data-target="1">0</span> unité de broderie
            </div>
          </div>
        </div>
      </section>
      <section className="certifications">
        <div className="container">
          <h2>{entreprise.certificationsTitle}</h2>
          <div className="certification-grid">
            <div className="certification-item">
              <img src={certImage1} alt="Certification WRAP" />
              <h3>{entreprise.certificationTitle1}</h3>
              <p>{entreprise.certification1}</p>
            </div>
            <div className="certification-item">
              <img src={certImage2} alt="Certification ISO 9001" />
              <h3>{entreprise.certificationTitle2}</h3>
              <p>{entreprise.certification2}</p>
            </div>
            <div className="certification-item">
              <img src={certImage3} alt="Certification OEKO-TEX" />
              <h3>{entreprise.certificationTitle3}</h3>
              <p>{entreprise.certification3}</p>
            </div>
            <div className="certification-item">
              <img src={certImage4} alt="Certification GOTS" />
              <h3>{entreprise.certificationTitle4}</h3>
              <p>{entreprise.certification4}</p>
            </div>
          </div>
        </div>
      </section>
      <section className="products">
        <div className="container">
          <h2>{entreprise.productsTitle}</h2>
          <div className="product-grid">
            <div className="product-item">
              <div className="product-mobile-title">
                <h3>{entreprise.productTitle1}</h3>
              </div>
              <img src={productImage1} alt="Vêtements Techniques" />
              <div className="content">
                <h3 className="product-desktop-title">{entreprise.productTitle1}</h3>
                <p>{entreprise.product1}</p>
                <Link to="/savoirfaire" className="btn-wrapper"><button className="btn">{entreprise.button}</button></Link>
              </div>
            </div>
            <div className="product-item">
              <div className="product-mobile-title">
                <h3>{entreprise.productTitle2}</h3>
              </div>
              <img src={productImage2} alt="Uniformes d'Intervention" />
              <div className="content">
                <h3 className="product-desktop-title">{entreprise.productTitle2}</h3>
                <p>{entreprise.product2}</p>
                <Link to="/savoirfaire" className="btn-wrapper"><button className="btn">{entreprise.button}</button></Link>
              </div>
            </div>
            <div className="product-item">
              <div className="product-mobile-title">
                <h3>{entreprise.productTitle3}</h3>
              </div>
              <img src={productImage3} alt="Uniformes de Restauration" />
              <div className="content">
                <h3 className="product-desktop-title">{entreprise.productTitle3}</h3>
                <p>{entreprise.product3}</p>
                <Link to="/savoirfaire" className="btn-wrapper"><button className="btn">{entreprise.button}</button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Entreprise;
